import React, { useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, ArrowLeft, CheckCircle2, ClipboardCheck, FlaskConical,
    Download, Info, Layers, Loader2, PencilRuler, Printer, ShieldAlert, Sparkles, Wand2,
} from 'lucide-react';
import { useAsplan } from './useAsplan';
import { useProjektBestand } from './useProjektBestand';
import { aktuelleKatalogVersion, freigabeSperrgrund } from './asplanLogic';
import { berechneTragezeit, GERAETEGRUPPEN, KORREKTUREN, schichtLeistung } from './tragezeit';
import { SkizzeSvg } from './skizzen/SkizzeSvg';
import { SCHLEUSE_STANDARD, pruefeSchleuse, zeichneSchleuse } from './skizzen/schleuse';
import { zeichneRaster } from './skizzen/raster';
import { zeichneSchnitt } from './skizzen/schnitt';
import { schnittParameterAusBereich } from './skizzen/aushub';
import { AblaufKarte, ZonenplanKarte } from './skizzen/SkizzenFelder';
import { AushaengeReiter } from './aushaenge/AushaengeReiter';
import {
    FormularAnzeige, FormularFilterbuch, FormularHeissarbeit, FormularSchleusenbuch,
    FormularTragezeit, FormularUebergabe, FormularUnterweisung, FormularVerpflichtung,
} from './formulare/Formulare';
import {
    atemschutzGeraete, bgDesUnternehmens, filterZeilen, verfuegbareFormulare,
} from './formulare/formularKatalog';
import {
    baueErkundungsPrompt, eingabeAusBundle, istLeer, leseErkundungsVorschlag,
    lueckenText, objektBeschreibung, type ErkundungsVorschlag,
} from './ki/historischeErkundung';
import { baubeschreibungHinweis, bedarfsanzeige, type Bedarfsanzeige } from './planungBedarf';
import { asplanHistorischeErkundung } from '../../services/geminiService';
import { useProjektSteckbrief } from '../../services/projektSteckbrief';
import { AbweichungsHinweis } from '../common/AbweichungsHinweis';
import { BefundeEditor } from './bearbeiten/BefundeEditor';
import { RollenEditor, StufungenEditor } from './bearbeiten/RollenEditor';
import { BausteineEditor } from './bearbeiten/BausteineEditor';
import { BestandKarte } from './bearbeiten/BestandKarte';
import { NotfallKarte } from './bearbeiten/NotfallKarte';
import { BereicheEditor } from './bearbeiten/BereicheEditor';
import { SchutzklassenEditor } from './bearbeiten/SchutzklassenEditor';
import {
    BeteiligteEditor, MesskonzeptEditor, PrueflisteEditor, UnterlagenEditor,
    UnternehmenEditor,
} from './bearbeiten/WeitereEditoren';
import { AblageKarte } from './ablage/AblageKarte';
import { asplanFreigabeAuftrag } from './ablage/asplanAblage';
import { freigabeWiederholung, halteFreigabeFest, type FreigabeFesthalten, type FreigabeJournal } from '../../services/unterlagenAblage';
import { selectBillableServices } from '../../services/billingSelection';
import { queueJournalMirror } from '../../services/serviceJournal';
import { useRechte } from '../../hooks/useRechte';
import { BillingSelectionModal } from '../BillingSelectionModal';
import { useBestandteilAbschluss } from '../bestandteile/useBestandteilAbschluss';
import { BestandteilAbschlussHinweis } from '../bestandteile/BestandteilAbschlussHinweis';
import { BestandteilStundenPflicht } from '../bestandteile/BestandteilStundenPflicht';
import { PAKET_NICHT_DIREKT, bestimmeAbschlussFall, mitPositionsauswahl, paketPositionIds, schonGebuchtHinweis } from '../bestandteile/abschlussLogic';
import { planeAsplanBuchung } from './asplanBuchung';
import { projektDesVorgangs, verworfenText } from '../bestandteile/abschlussSchreibweg';
import type { Report } from '../../types';
import { buildCompanyForPdf } from '../../services/pdf/companyForPdf';
import { buildActorName } from '../../services/actorName';
import { asplanDateiname, generateAsplanPdf } from '../../services/pdf/generatorAsplan';
import { gefahrstoff } from './kataloge/gefahrstoffe';
import type { SchleusenParameter } from './types';

/**
 * A+S-Plan nach DGUV Regel 101-004 / TRGS 524 — duenner Container.
 *
 * Die Fachlogik liegt vollstaendig daneben und ist ohne DOM geprueft
 * (`einstufung.ts`, `schutzklassen.ts`, `tragezeit.ts`, `asplanLogic.ts`,
 * `skizzen/*`). Diese Datei ordnet an, zeigt an und ruft auf — sie entscheidet
 * nichts. Dieselbe Aufteilung wie bei Handbuch, Koordinierung und
 * Planungsphase, und aus demselben Grund: ohne jsdom im Projekt ist alles
 * testbar, was nicht in einer Komponente steht.
 *
 * ═══ DIE REIHENFOLGE DER KAPITEL ══════════════════════════════════════════
 * Sie folgt der Sacharbeit und nicht der Nummerierung des gedruckten Plans:
 * zuerst die Befunde, dann die Bereiche, dann die Taetigkeiten, dann der
 * Schutz. Wer mit den Schutzmassnahmen anfaengt, schreibt sie aus dem
 * Vorprojekt ab — genau der Mangel, den die BG BAU benennt.
 */

interface Props {
    projectId: string;
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    /**
     * `id` wird beim Ablegen in die Projektablage als `hochgeladen_von`
     * vermerkt; `role` steuert Preise im Abrechnungsdialog und den
     * Abschlussschritt (kein Schritt fuer Freelancer).
     */
    userProfile?: { id?: string; role?: string | null; first_name?: string | null; last_name?: string | null } | null;
    /** Firmenstammdaten aus dem Profil (App.invoiceCompany). */
    company?: unknown;
    /** Briefkopf-Konfiguration (App.quoteConfigMemo). */
    quoteConfig?: unknown;
    /** Rechnungs-Stammdaten fuer die Fusszeile (App.invoiceConfigMemo). */
    invoiceConfig?: unknown;
    onBack: () => void;
    isReadOnly?: boolean;
    /**
     * Das Projekt mit Leistungsstand (`metadata.globalData.services`) und der
     * Schreibweg (App.handleUpdateReport) — fuer den Abschlussschritt nach der
     * Freigabe (Plan Paketposition B5). Fehlt eins, bucht das Modul nichts.
     */
    report?: Report | null;
    onUpdate?: (report: Report) => void;
}

type Reiter = 'ueberblick' | 'grundlagen' | 'befunde' | 'bereiche' | 'rollen' | 'schutz'
    | 'texte' | 'messung' | 'beteiligte' | 'zeichnungen' | 'aushaenge' | 'formulare' | 'check';

const REITER: { key: Reiter; label: string; icon: React.ReactNode }[] = [
    { key: 'ueberblick', label: 'Überblick', icon: <Info className="w-3.5 h-3.5" /> },
    { key: 'grundlagen', label: 'Grundlagen', icon: <Sparkles className="w-3.5 h-3.5" /> },
    { key: 'befunde', label: 'Befunde', icon: <FlaskConical className="w-3.5 h-3.5" /> },
    { key: 'bereiche', label: 'Arbeitsbereiche', icon: <Layers className="w-3.5 h-3.5" /> },
    { key: 'rollen', label: 'Rollen', icon: <Layers className="w-3.5 h-3.5" /> },
    { key: 'schutz', label: 'Schutzklassen', icon: <ShieldAlert className="w-3.5 h-3.5" /> },
    { key: 'texte', label: 'Textkapitel', icon: <Sparkles className="w-3.5 h-3.5" /> },
    { key: 'messung', label: 'Messkonzept', icon: <Info className="w-3.5 h-3.5" /> },
    { key: 'beteiligte', label: 'Beteiligte', icon: <Info className="w-3.5 h-3.5" /> },
    { key: 'zeichnungen', label: 'Zeichnungen', icon: <PencilRuler className="w-3.5 h-3.5" /> },
    { key: 'aushaenge', label: 'Aushänge', icon: <Printer className="w-3.5 h-3.5" /> },
    { key: 'formulare', label: 'Formulare', icon: <ClipboardCheck className="w-3.5 h-3.5" /> },
    { key: 'check', label: 'Selbstcheck', icon: <ClipboardCheck className="w-3.5 h-3.5" /> },
];

export const AsplanModul: React.FC<Props> = ({
    projectId, projektName, projektNummer, projektAdresse, userProfile,
    company, quoteConfig, invoiceConfig, onBack, isReadOnly = false,
    report = null, onUpdate,
}) => {
    const a = useAsplan(projectId, isReadOnly);
    /**
     * ABSCHLUSSSCHRITT NACH DER FREIGABE (Plan Paketposition B5). Der A+S-Plan
     * bucht seit dem Betreiberentscheid vom 19.10.2026 direkt nach der
     * Freigabe — vorher „zaehlte er nur":
     *  - Fall 1/3: eigene Position mit der Zuordnung `asplan` → die
     *    Positionsauswahl (BillingModule 'asplan', kein Textarm), Preise nur
     *    mit dem Recht „Preise sehen";
     *  - Fall 2/3: die Ausgabe der Freigabe erledigt den Bestandteil
     *    „A+S-Plan" per Trigger; der Hook meldet das und bucht ein dadurch
     *    vollstaendiges Paket;
     *  - Nur-Ansicht, Freelancer, ohne Schreibweg: kein Schritt.
     */
    const rechte = useRechte(userProfile?.role ?? null);
    const abschluss = useBestandteilAbschluss({ report, onUpdate, userProfile, quelle: 'asplan', isReadOnly, projectId });
    /**
     * Offen nach einer Freigabe mit eigener Position; traegt die Fassung fuer
     * den Protokolltext und das PROJEKT des Vorgangs (bei der Freigabe
     * festgehalten — geschrieben wird nur in dieses, Codex R2b).
     */
    const [buchungsSchritt, setBuchungsSchritt] = useState<{ revLabel: string | null; projektId: string } | null>(null);
    const [buchungsMeldung, setBuchungsMeldung] = useState<{ ton: 'ok' | 'fehler'; text: string } | null>(null);
    const asplanPositionen = useMemo(
        () => selectBillableServices(report?.metadata?.globalData?.services || [], 'asplan'),
        [report?.metadata?.globalData?.services],
    );
    /**
     * Stammdaten und Notrufplan des Projekts — EINMAL fuer das ganze Modul.
     *
     * Vorher las jede Karte selbst (Notfallkarte, Bestandskarte, Aushangreiter
     * mit einem eigenen Hook): drei unabhaengige Staende derselben Quelle, eine
     * neue Abfrage bei jedem Reiterwechsel, und im schlimmsten Fall zeigte der
     * Reiter „Grundlagen" andere Notfallnummern als das Blatt, das im Reiter
     * „Aushaenge" gedruckt wurde. Der duenne Container haelt den Zustand, die
     * Karten bekommen ihn als Prop — dieselbe Doktrin wie im ProjectHub.
     */
    const bestand = useProjektBestand(projectId);
    /**
     * Der Projektsteckbrief — Bedarf, Baubeschreibung und Unterlagenstatus
     * der Planungsphase. EINMAL gelesen und als Prop weitergereicht, aus
     * demselben Grund wie `bestand`. Die Projektdaten kommen aus `bestand`,
     * damit der Rueckfall der Baubeschreibung (Projektdaten) sichtbar wird.
     * Er liest nur; uebernommen wird ausschliesslich per Klick.
     */
    const steckbrief = useProjektSteckbrief(projectId, bestand.metadata);
    const bedarf = useMemo(() => bedarfsanzeige(steckbrief.steckbrief), [steckbrief.steckbrief]);
    const [reiter, setReiter] = useState<Reiter>('ueberblick');
    const [schleuse, setSchleuse] = useState<SchleusenParameter>(SCHLEUSE_STANDARD);
    /**
     * Uebernimmt die gespeicherte Schleuseneinstellung EINMAL nach dem Laden.
     *
     * Nicht bei jedem Durchlauf: sonst uebersteuerte der gespeicherte Stand
     * jede Aenderung, die gerade vorgenommen wird — der Anwender koennte die
     * Kammern nicht mehr umstellen.
     */
    const [schleuseGeladen, setSchleuseGeladen] = useState(false);
    const [geraet, setGeraet] = useState('filter_vollmaske');
    const [korrekturen, setKorrekturen] = useState<string[]>([]);
    const [revGrund, setRevGrund] = useState('');
    /**
     * Der Planungsstand der letzten Freigabe konnte nicht festgehalten werden
     * (Stufe 6.1, `halteFreigabeFest`). Die Freigabe gilt; der Satz steht an
     * ihr, bis die naechste Freigabe ihn ersetzt. `wiederholung` ist der
     * Auftrag fuer „Erneut festhalten" mit GENAU dem Anker des ersten
     * Versuchs — `null`, wenn keiner entstand (dann nur der Hinweis).
     */
    const [freigabeJournal, setFreigabeJournal] = useState<{ hinweis: string; wiederholung: FreigabeFesthalten | null } | null>(null);
    const [journalLaeuft, setJournalLaeuft] = useState(false);
    /**
     * Auslöser fuer den Aenderungsdienst (`AbweichungsHinweis`): nach
     * Freigabe-Journal, „Erneut festhalten" und „Neue Revision" hochgezaehlt,
     * damit der Hinweis die NEUE Fassung vergleicht — nicht die alte, deren
     * Entscheid die frische Freigabe gleich wieder eroeffnete.
     */
    const [abgleichLauf, setAbgleichLauf] = useState(0);
    const abgleichNeu = () => setAbgleichLauf(n => n + 1);

    /**
     * Schreibt das Freigabe-Journal — erster Versuch und „Erneut festhalten"
     * gleich. Gibt das Ergebnis zurueck: seine Ausgaben-Kennungen braucht der
     * Abschlussschritt (Plan B5).
     */
    const halteFest = async (auftrag: FreigabeFesthalten): Promise<FreigabeJournal> => {
        setJournalLaeuft(true);
        try {
            const journal = await halteFreigabeFest(auftrag);
            setFreigabeJournal(journal.status === 'fehler'
                ? { hinweis: journal.hinweis || 'Der Planungsstand wurde nicht festgehalten.', wiederholung: freigabeWiederholung(auftrag, journal) }
                : null);
            return journal;
        } finally {
            setJournalLaeuft(false);
            abgleichNeu();
        }
    };

    /** „Erneut festhalten": erst jetzt liegt die Ausgabe — die Bestandteile nachfragen (Plan B5). */
    const halteErneutFest = (auftrag: FreigabeFesthalten) => {
        void halteFest(auftrag).then(journal => {
            if (journal.status !== 'fehler') void abschluss.nachAblage(journal);
        });
    };

    /**
     * Fall 1/3 (Plan B5): die Buchung auf die gewaehlte `asplan`-Position —
     * auf dem Stand, der JETZT gilt (`holeBericht`), ueber `schreibe` (dieselbe
     * Ref, ueber die der Abschlussschritt gleichzeitig ein Paket bucht), dann
     * die Zweitschrift ins Buchungsjournal (Quelle 'asplan', Muster B).
     * „Ohne Leistungsbuchung" (`serviceId` null) bucht nichts.
     */
    const bucheAsplan = (serviceId: string | null, hours: number) => {
        const schritt = buchungsSchritt;
        setBuchungsSchritt(null);
        if (!serviceId || !schritt || isReadOnly || !onUpdate) return;
        const stand = abschluss.holeBericht();
        if (paketPositionIds(stand?.metadata?.globalData?.services).has(serviceId)) {
            setBuchungsMeldung({ ton: 'fehler', text: PAKET_NICHT_DIREKT });
            return;
        }
        const plan = stand && planeAsplanBuchung({
            report: stand,
            serviceId,
            hours,
            revLabel: schritt.revLabel,
            performerName: benutzer || stand.metadata.coordinator || '',
        });
        if (!plan) {
            setBuchungsMeldung({ ton: 'fehler', text: 'Die gewählte Position wurde im Projekt nicht gefunden — es wurde nichts gebucht.' });
            return;
        }
        // Nur in das Projekt der Freigabe — sonst nichts, auch kein Journal.
        const verworfen = abschluss.schreibeFuer(schritt.projektId, plan.report);
        if (verworfen) {
            setBuchungsMeldung({ ton: 'fehler', text: verworfenText(verworfen, 'position') });
            return;
        }
        if (plan.vormerkung) queueJournalMirror(plan.vormerkung);
        else console.warn('[AsplanModul] Buchung ohne belastbare Projekt-id — nicht im Journal vorgemerkt.');
        const position = (stand.metadata.globalData?.services || []).find(s => s.id === serviceId);
        setBuchungsMeldung({
            ton: 'ok',
            text: `A+S-Plan gebucht: ${plan.logEntry.quantity} ${position?.unit || 'Stück'} auf „${position?.title || 'Position'}“.`,
        });
    };

    /** Eine Revision eroeffnen — und den Aenderungsdienst danach neu lesen lassen. */
    const eroeffneRevision = (grund: string): Promise<unknown> =>
        a.revisionEroeffnen(benutzer, grund).then(p => { if (p) abgleichNeu(); return p; });

    /**
     * Freigabe — und unmittelbar danach der Planungsstand DIESER Freigabe fuer
     * den Aenderungsdienst. Nicht erst beim Ablegen: „Plan ablegen" kann
     * beliebig lange spaeter kommen und friere dann den heutigen Stand fuer
     * eine alte Freigabe ein (Codex-Review 17.10.2026). Der Fassungsname wird
     * danach geleert: er ist die Kennung der Freigabe und darf nicht in die
     * naechste wandern (`freigabeSperrgrund`).
     */
    const gibFrei = () => {
        setFreigabeJournal(null);
        setBuchungsMeldung(null);
        // Der Fall steht beim KLICK fest — die Freigabe aendert das LV nicht.
        const fall = bestimmeAbschlussFall({ positionen: asplanPositionen, pakete: abschluss.pakete });
        void a.freigeben(benutzer, revGrund.trim()).then(async plan => {
            if (!plan) return;
            setRevGrund('');
            const journal = await halteFest(asplanFreigabeAuftrag(plan, bestand.metadata));
            // Abschlussschritt (Plan B5): Bestandteile nachfragen (mit den
            // Kennungen der Ausgaben dieser Freigabe) und — bei eigener
            // Position — die Buchung anbieten. Unabhaengig vom Journal: die
            // eigene Position bucht auch, wenn der Planungsstand fehlt.
            void abschluss.nachAblage(journal);
            if (!abschluss.sperre && mitPositionsauswahl(fall)) {
                setBuchungsSchritt({ revLabel: plan.rev_label || null, projektId: projektDesVorgangs(abschluss.holeBericht()) });
            }
        });
    };
    /**
     * Satz nach „Neue Fassung anlegen" im Abweichungshinweis (Stufe 6.1):
     * entweder, dass die Revision eroeffnet wurde, oder dass der Plan schon
     * im Entwurf ist. Gesetzt nur per Klick, nie beim Laden.
     */
    const [neueFassungHinweis, setNeueFassungHinweis] = useState<string | null>(null);
    const [vorschlag, setVorschlag] = useState<ErkundungsVorschlag | null>(null);
    const [kiLaeuft, setKiLaeuft] = useState(false);
    const [kiFehler, setKiFehler] = useState<string | null>(null);

    const b = a.bundle;
    // Das Profil fuehrt Vor- und Nachname, kein `full_name`/`name` — die
    // fruehere Lesart ergab immer '' und liess den Namen in der Historie leer.
    const benutzer = buildActorName(userProfile);

    // ── Zeichnungen ────────────────────────────────────────────────────────
    useEffect(() => {
        if (schleuseGeladen || !a.bundle) return;
        const gespeichert = a.bundle.skizzen.find(x => x.typ === 'schleuse');
        if (gespeichert && Object.keys(gespeichert.parameter).length > 0) {
            setSchleuse(gespeichert.parameter as unknown as SchleusenParameter);
        }
        setSchleuseGeladen(true);
    }, [a.bundle, schleuseGeladen]);

    const schleusenBild = useMemo(() => zeichneSchleuse(schleuse), [schleuse]);
    const schleusenFehler = useMemo(() => pruefeSchleuse(schleuse), [schleuse]);

    const rasterBild = useMemo(() => {
        if (!b) return null;
        const zuBereich = new Map<string, string>();
        for (const c of b.cluster) for (const f of c.felder) zuBereich.set(f, c.kennung);
        const abgegrenzt = new Set(
            b.cluster.filter(c => c.art === 'abgegrenzt').flatMap(c => c.felder));

        return zeichneRaster(b.rasterfelder.map(f => ({
            feld_nr: f.feld_nr,
            spalte: f.raster_spalte,
            zeile: f.raster_zeile,
            einstufung: a.feldLage.get(f.feld_nr)?.klasse ?? null,
            vorbehalt: a.feldLage.get(f.feld_nr)?.vorbehalt ?? false,
            cluster_kennung: zuBereich.get(f.feld_nr),
            abgegrenzt: abgegrenzt.has(f.feld_nr),
        })));
    }, [b, a.feldLage]);

    /** Die Namen der gefuehrten Stoffe — sie stehen auf mehreren Aushaengen
     *  und im Notfallausweis, deshalb einmal berechnet. */
    const aktiveStoffnamen = useMemo(
        () => (b?.plan.stoffe ?? [])
            .filter(x => x.aktiv)
            .map(x => gefahrstoff(x.key)?.name ?? x.key),
        [b],
    );

    const tragezeit = useMemo(() => berechneTragezeit(geraet, korrekturen), [geraet, korrekturen]);
    const schicht = useMemo(() => schichtLeistung(tragezeit, 480), [tragezeit]);

    /**
     * Laesst die KI einen Vorschlag zur Historischen Erkundung erarbeiten.
     *
     * Der Vorschlag wird NICHT gespeichert. Er erscheint neben den Feldern,
     * und der Bearbeiter uebernimmt einzeln, was er verantworten kann — bei
     * einer Erkundung ist die Verantwortung fuer den Wortlaut der Kern der
     * Leistung, nicht der Wortlaut selbst.
     */
    const erkundungVorschlagen = async () => {
        if (!b) return;
        setKiLaeuft(true);
        setKiFehler(null);
        try {
            const eingabe = eingabeAusBundle(b, projektName ?? '', projektNummer, projektAdresse);
            const roh = await asplanHistorischeErkundung(baueErkundungsPrompt(eingabe));
            const gelesen = leseErkundungsVorschlag(roh);
            if (istLeer(gelesen)) {
                setKiFehler('Die KI hat nichts Verwertbares geliefert. Tragen Sie ein, was Sie '
                    + 'über die bisherige Nutzung wissen — daraus lässt sich mehr machen.');
            } else {
                setVorschlag(gelesen);
            }
        } catch (e) {
            const text = (e as Error).message || '';
            setKiFehler(text.includes('Unbekannte Action')
                ? 'Die KI-Funktion ist auf dem Server noch nicht ausgerollt '
                  + '(gemini-proxy neu deployen).'
                : text);
        } finally {
            setKiLaeuft(false);
        }
    };

    /**
     * Erzeugt das Plan-PDF und laedt es herunter.
     *
     * Bewusst OHNE Freigabepflicht: ein Entwurf muss sich ausdrucken lassen,
     * sonst kann ihn niemand gegenlesen. Dass es ein Entwurf ist, steht im
     * Kopf des Dokuments — und die offenen Punkte stehen ausdruecklich in den
     * Kapiteln, in die sie gehoeren.
     */
    const exportiere = () => {
        if (!b) return;
        const pdfCompany = buildCompanyForPdf(
            company as never, quoteConfig as never, invoiceConfig as never,
        );
        const doc = generateAsplanPdf(b, pdfCompany, {
            projektName,
            projektNummer,
            bauvorhabenAdresse: projektAdresse,
            schleuse,
            // Die Rettungskette (Anhang 3) kommt aus dem Notrufplan des
            // SiGe-Plans — gelesen wird er hier, gedruckt wird er dort. Vor
            // der Antwort wird `undefined` uebergeben und NICHT `null`: der
            // Plan soll offenlassen, was er nicht weiss, statt zu behaupten,
            // es sei keine Notfallstelle hinterlegt.
            notrufplan: bestand.bereit ? bestand.notrufplan : undefined,
        });
        doc.save(asplanDateiname(
            projektName || 'Projekt',
            b.plan.rev_label,
            b.plan.stand ?? undefined,
        ));
    };

    // ═══════════════════════════════════════════════════════════════════════

    if (a.laedt) {
        return <div className="h-full flex items-center justify-center text-slate-400">Lädt …</div>;
    }

    if (!b) {
        return (
            <Rahmen onBack={onBack} titel="A+S-Plan" untertitel={projektName}>
                {/* Auch ohne Plan: ob die Planungsphase einen braucht, ist
                    genau hier die Frage. */}
                <BedarfsHinweis bedarf={bedarf} bereit={steckbrief.bereit} />
                <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center max-w-xl mx-auto">
                    <ShieldAlert className="w-10 h-10 text-stone-400 mx-auto mb-3" />
                    <h2 className="font-bold text-slate-800 mb-1">Für dieses Projekt gibt es noch keinen A+S-Plan</h2>
                    <p className="text-sm text-slate-500 mb-5">
                        Der Arbeits- und Sicherheitsplan nach DGUV Regel 101-004 wird gebraucht, sobald in
                        kontaminierten Bereichen gearbeitet wird — unabhängig davon, wie viele Unternehmen
                        beteiligt sind.
                    </p>
                    {a.fehler && <Fehlerzeile text={a.fehler} />}
                    <button
                        type="button"
                        disabled={isReadOnly || a.arbeitet}
                        onClick={() => void a.anlegen(`A+S-Plan ${projektName ?? ''}`.trim())}
                        className="px-4 py-2.5 bg-stone-700 text-white rounded-xl text-sm font-bold hover:bg-stone-800 disabled:opacity-40"
                    >
                        A+S-Plan anlegen
                    </button>
                </div>
            </Rahmen>
        );
    }

    const plan = b.plan;
    const gesperrt = plan.status === 'freigegeben';

    /**
     * „Neue Fassung anlegen" aus dem Abweichungshinweis: der vorhandene
     * Revisionsweg. Ist der Plan freigegeben, wird die Revision eroeffnet —
     * die freigegebene Fassung bleibt erhalten (Hausregel des Moduls). Ist er
     * schon Entwurf, gibt es nichts zu eroeffnen; der Satz sagt, was zu tun ist.
     */
    const neueFassungAusPlanung = () => {
        if (isReadOnly) return;
        if (!gesperrt) {
            setNeueFassungHinweis('Der Plan ist bereits im Entwurf — die Änderungen aus der Planungsphase einarbeiten und erneut freigeben.');
            return;
        }
        // Der Satz erst nach dem Erfolg — scheitert das Schreiben, steht der
        // Grund in `a.fehler`, und nichts behauptet eine Revision, die es nicht gibt.
        void eroeffneRevision('Neue Revision: Änderung in der Planungsphase').then(p => {
            if (p) setNeueFassungHinweis('Neue Revision eröffnet wegen Änderung in der Planungsphase. Die freigegebene Fassung bleibt erhalten, bis die neue freigegeben ist.');
        });
    };

    /** Sperrgrund fuer „Freigeben": vergebener Fassungsname oder noch nicht gelesene Stammdaten. */
    const freigabeSperre = !gesperrt
        ? (freigabeSperrgrund(plan.historie, revGrund)
            || (bestand.bereit ? null : 'Die Stammdaten des Projekts werden noch gelesen — ohne sie kann der Planungsstand der Freigabe nicht festgehalten werden.'))
        : null;

    return (
        <Rahmen
            onBack={onBack}
            titel="A+S-Plan"
            untertitel={`${projektName || 'Projekt'}${projektNummer ? ` · ${projektNummer}` : ''} · `
                + `DGUV Regel 101-004 / TRGS 524 (${plan.regelwerk_fassung === '2026-07' ? 'Fassung 07/2026' : 'Vorfassung'})`}
        >
            {isReadOnly && (
                <Hinweis ton="amber">
                    Nur-Ansicht: Der Plan lässt sich ansehen und drucken, Änderungen werden nicht gespeichert.
                </Hinweis>
            )}
            {gesperrt && (
                <Hinweis ton="emerald">
                    Freigegeben {plan.rev_label && `als ${plan.rev_label}`} von {plan.freigegeben_von_name}.
                    Für Änderungen eine neue Revision eröffnen — die freigegebene Fassung bleibt erhalten.
                </Hinweis>
            )}
            {a.fehler && <Fehlerzeile text={a.fehler} />}
            {/* Stunden-Pflichtfenster (Stundenkontingent S3) — genau einmal je Modul,
                auf jedem Reiter (der Hinweis steht nur im Überblick). */}
            <BestandteilStundenPflicht pflicht={abschluss.stundenPflicht} />
            {/* Abschlussschritt nach der Freigabe (Plan B5, Fall 1/3): nur Positionen
                mit der Zuordnung `asplan`; „bereits gebucht" steht vorher da. */}
            {buchungsSchritt && (
                <BillingSelectionModal
                    isOpen
                    onClose={() => setBuchungsSchritt(null)}
                    onConfirm={bucheAsplan}
                    services={asplanPositionen}
                    title="A+S-Plan abrechnen"
                    context="asplan"
                    memoryScope={projectId}
                    canSeeFinancials={rechte.hat('preise.sehen')}
                    vorabHinweis={schonGebuchtHinweis(asplanPositionen)}
                />
            )}
            <BedarfsHinweis bedarf={bedarf} bereit={steckbrief.bereit} />
            {/* Aenderungsdienst (Stufe 6.1): weicht die freigegebene Fassung vom
                heutigen Stand der Planungsphase ab, steht die Rueckfrage hier im
                Kopf. Nur lesend; geschrieben wird dort allein der Entscheid. */}
            {projectId && projectId !== 'temp-id' && (
                <AbweichungsHinweis
                    projectId={projectId}
                    metadata={bestand.metadata}
                    unterlage="asplan"
                    isReadOnly={isReadOnly}
                    onNeueFassung={neueFassungAusPlanung}
                    neuladen={abgleichLauf}
                    sperrgrund={a.arbeitet || journalLaeuft
                        ? 'Der Plan wird gerade geschrieben bzw. die Freigabe festgehalten — ein Entscheid ist erst danach möglich.'
                        : null}
                />
            )}
            {neueFassungHinweis && (
                <div className="mb-4 rounded-xl px-4 py-2.5 text-sm border bg-blue-50 border-blue-200 text-blue-900 flex items-start gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="flex-1 min-w-0">{neueFassungHinweis}</span>
                    <button
                        type="button"
                        onClick={() => setNeueFassungHinweis(null)}
                        title="Hinweis ausblenden"
                        className="shrink-0 text-xs font-bold hover:underline"
                    >
                        Ausblenden
                    </button>
                </div>
            )}

            <div className="flex justify-end gap-2 mb-3">
                {/* Der Weg in die Projektablage fuehrt bewusst auf den Reiter
                    und legt nicht von hier aus ab: dort steht daneben, was die
                    Anlagenliste des Plans daraus macht. Ein Knopf, der still
                    eine Datei schreibt, zeigt das nicht. */}
                <button
                    type="button"
                    onClick={() => setReiter('beteiligte')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                    <ClipboardCheck className="w-3.5 h-3.5" /> Druckstücke und Nachweise
                </button>
                <button
                    type="button"
                    onClick={exportiere}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                    <Download className="w-3.5 h-3.5" /> Plan als PDF
                </button>
            </div>

            {/* ─── Kennzahlen ───────────────────────────────────────────── */}
            {a.stand && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    <Kachel wert={a.stand.schwer} label="zu klären" ton={a.stand.schwer > 0 ? 'rot' : 'gruen'} />
                    <Kachel wert={a.stand.mittel} label="Widersprüche" ton={a.stand.mittel > 0 ? 'amber' : 'gruen'} />
                    <Kachel wert={a.stand.hinweise} label="Hinweise" ton="grau" />
                    <Kachel
                        wert={`${a.stand.checks_erfuellt}/${a.stand.checks_gesamt}`}
                        label="Prüfpunkte erfüllt"
                        ton={a.stand.checks_erfuellt === a.stand.checks_gesamt ? 'gruen' : 'amber'}
                    />
                </div>
            )}

            {/* ─── Reiter ───────────────────────────────────────────────── */}
            <div data-glass="reiter" className="flex gap-1 mb-4 overflow-x-auto pb-1">
                {REITER.map(r => (
                    <button
                        key={r.key}
                        type="button"
                        onClick={() => setReiter(r.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap ${
                            reiter === r.key
                                ? 'bg-stone-700 text-white'
                                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                        {r.icon} {r.label}
                    </button>
                ))}
            </div>

            {/* ─── Überblick ────────────────────────────────────────────── */}
            {reiter === 'ueberblick' && (
                <>
                <Karte
                    titel="Stand und Revision"
                    hinweis="Eine Freigabe friert den Plan ein. Spätere Änderungen erzeugen eine neue Revision — die freigegebene Fassung bleibt erhalten, wie beim Musterplan Rev. 3.1."
                >
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="text-sm text-slate-700">
                            <p><b>Status:</b> {gesperrt ? `freigegeben (${plan.rev_label || 'ohne Kennung'})` : 'Entwurf'}</p>
                            <p className="text-xs text-slate-500">
                                {plan.historie.length} Eintrag(e) in der Änderungshistorie
                            </p>
                        </div>
                        <div className="flex-1" />
                        {!isReadOnly && (gesperrt ? (
                            <button
                                type="button"
                                disabled={a.arbeitet}
                                onClick={() => void eroeffneRevision(revGrund || 'Neue Revision eröffnet')}
                                className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-bold hover:bg-slate-50"
                            >
                                Neue Revision eröffnen
                            </button>
                        ) : (
                            <div className="flex items-end gap-2">
                                <label className="block">
                                    <span className="block text-[11px] font-bold text-slate-600 mb-1">Fassung</span>
                                    <input
                                        value={revGrund}
                                        onChange={e => setRevGrund(e.target.value)}
                                        placeholder="Rev. 1.0"
                                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm w-32"
                                    />
                                </label>
                                <button
                                    type="button"
                                    disabled={a.arbeitet || journalLaeuft || !a.stand?.freigabe_moeglich || !revGrund.trim() || !!freigabeSperre}
                                    title={freigabeSperre
                                        || (a.stand?.freigabe_moeglich
                                            ? undefined
                                            : 'Erst die zu klärenden Punkte im Überblick abarbeiten.')}
                                    onClick={gibFrei}
                                    className="px-3 py-2 bg-stone-700 text-white rounded-lg text-xs font-bold disabled:opacity-40"
                                >
                                    Freigeben
                                </button>
                            </div>
                        ))}
                    </div>
                    {!gesperrt && a.stand && !a.stand.freigabe_moeglich && (
                        <p className="text-xs text-amber-700 mt-2">
                            {a.stand.schwer} Punkt(e) sind vor der Freigabe zu klären — sie stehen unten.
                        </p>
                    )}
                    {/* Sichtbarer Grund statt eines stummen Knopfes: vergebener Fassungsname oder Stammdaten noch unterwegs. */}
                    {freigabeSperre && (
                        <p className="text-xs text-amber-700 mt-2">{freigabeSperre}</p>
                    )}
                    {/* Das Freigabe-Journal (Stufe 6.1) konnte nicht geschrieben werden:
                        die Freigabe gilt, die Fassung wird aber nicht auf Aenderungen ueberwacht.
                        Mit Anker des ersten Versuchs laesst es sich wiederholen — ohne neuen Auszug. */}
                    {/* Abschlussschritt (Plan B5): Ergebnis der Buchung und was die
                        Freigabe an Bestandteilen einer Paketposition erledigt hat. */}
                    {buchungsMeldung && (
                        <p className={`text-xs mt-2 ${buchungsMeldung.ton === 'ok' ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {buchungsMeldung.text}
                        </p>
                    )}
                    <BestandteilAbschlussHinweis stand={abschluss.stand} onSchliessen={abschluss.zuruecksetzen} className="mt-2" />
                    {freigabeJournal && (
                        <div className="text-xs text-amber-700 mt-2 flex flex-wrap items-center gap-2">
                            <span className="min-w-0">{freigabeJournal.hinweis}</span>
                            {freigabeJournal.wiederholung && !isReadOnly && (
                                <button
                                    type="button"
                                    disabled={journalLaeuft}
                                    onClick={() => { const w = freigabeJournal.wiederholung; if (w) halteErneutFest(w); }}
                                    className="px-2.5 py-1 bg-white border border-amber-300 rounded-lg text-[11px] font-bold hover:bg-amber-50 disabled:opacity-40"
                                >
                                    {journalLaeuft ? 'Wird festgehalten …' : 'Erneut festhalten'}
                                </button>
                            )}
                        </div>
                    )}
                </Karte>

                <Karte titel="Was noch zu klären ist">
                    {a.befunde.length === 0 ? (
                        <p className="text-sm text-emerald-700 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" /> Keine offenen Widersprüche.
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {a.befunde.map(w => (
                                <li
                                    key={w.key}
                                    className={`rounded-lg border px-3 py-2.5 ${
                                        w.schwere === 'schwer' ? 'border-red-200 bg-red-50'
                                            : w.schwere === 'mittel' ? 'border-amber-200 bg-amber-50'
                                                : 'border-slate-200 bg-slate-50'
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
                                            w.schwere === 'schwer' ? 'text-red-600'
                                                : w.schwere === 'mittel' ? 'text-amber-600' : 'text-slate-400'
                                        }`} />
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-slate-800">{w.text}</p>
                                            <p className="text-xs text-slate-600 mt-0.5">{w.was_tun}</p>
                                            <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                                                {w.kapitel}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Karte>
                </>
            )}

            {/* ─── Grundlagen: Bauvorhaben ──────────────────────────────── */}
            {/* Das Feld war bis hierher in keiner Komponente bearbeitbar, die
                KI-Erkundung bekam fuer „Geplante Massnahme" immer nichts. Die
                Baubeschreibung der Planungsphase kommt nur per Klick herein. */}
            {reiter === 'grundlagen' && (() => {
                const beschreibung = objektBeschreibung(plan.objekt);
                const hinweis = steckbrief.bereit ? baubeschreibungHinweis(steckbrief.steckbrief, beschreibung) : null;
                const beschreibungSpeichern = (v: string) => void a.planAendern({
                    objekt: { ...plan.objekt, beschreibung: v },
                });
                return (
                    <Karte
                        titel="Bauvorhaben"
                        hinweis="Was gebaut wird, in wenigen Sätzen. Die KI-Erkundung liest es als „Geplante Maßnahme“ — keine Personendaten eintragen."
                    >
                        <Feld
                            label="Beschreibung des Bauvorhabens"
                            mehrzeilig
                            wert={beschreibung}
                            gesperrt={gesperrt || isReadOnly}
                            onSpeichern={beschreibungSpeichern}
                        />
                        {!steckbrief.bereit && !beschreibung && (
                            <p className="text-[11px] text-slate-400">Die Planungsphase wird gelesen …</p>
                        )}
                        {hinweis?.art === 'vorschlag' && (
                            <VorschlagsBlock
                                titel={`Baubeschreibung aus der Planungsphase (${hinweis.quelle})`}
                                text={hinweis.text}
                                vorbehalt={hinweis.vorbehalt ?? undefined}
                                knopf="aus der Planungsphase übernehmen"
                                gesperrt={gesperrt || isReadOnly}
                                onUebernehmen={() => beschreibungSpeichern(hinweis.text)}
                            />
                        )}
                        {hinweis?.art === 'fehlt' && (
                            <p className="text-[11px] text-slate-500">
                                Aus der Planungsphase lässt sich nichts übernehmen: {hinweis.grund}
                            </p>
                        )}
                    </Karte>
                );
            })()}

            {/* ─── Grundlagen: Historische Erkundung ────────────────────── */}
            {reiter === 'grundlagen' && (
                <Karte
                    titel="Historische Erkundung"
                    hinweis="Ohne sie ist die Auswahl der untersuchten Stoffe nicht begründbar — und die Analytik trifft möglicherweise am tatsächlichen Stoffspektrum vorbei."
                >
                    <Feld
                        label="Nutzungsgeschichte"
                        mehrzeilig
                        wert={String(plan.historische_erkundung.nutzungsgeschichte ?? '')}
                        gesperrt={gesperrt || isReadOnly}
                        onSpeichern={v => void a.planAendern({
                            historische_erkundung: { ...plan.historische_erkundung, nutzungsgeschichte: v },
                        })}
                    />
                    <Feld
                        label="Was nicht ermittelbar war"
                        mehrzeilig
                        wert={String(plan.historische_erkundung.luecken ?? '')}
                        gesperrt={gesperrt || isReadOnly}
                        hinweis="Häufig liegen die Daten nicht mehr vor. Das ist eine Aussage, die in den Plan gehört."
                        onSpeichern={v => void a.planAendern({
                            historische_erkundung: { ...plan.historische_erkundung, luecken: v },
                        })}
                    />
                    <Feld
                        label="Welche Folge daraus gezogen wird"
                        mehrzeilig
                        wert={String(plan.historische_erkundung.luecken_folge ?? '')}
                        gesperrt={gesperrt || isReadOnly}
                        hinweis="Vorsorgeprinzip, erweiterte Analytik, vorlaufende Sondierung."
                        onSpeichern={v => void a.planAendern({
                            historische_erkundung: { ...plan.historische_erkundung, luecken_folge: v },
                        })}
                    />
                </Karte>
            )}

            {reiter === 'grundlagen' && (
                <Karte
                    titel="Vorschlag der KI"
                    hinweis="Die KI ordnet Ihre Angaben, benennt offene Recherchefragen und leitet aus genannten Nutzungen das zu erwartende Stoffspektrum ab. Was auf dem Grundstück tatsächlich betrieben wurde, kann sie nicht wissen — sie darf es hier auch nicht behaupten."
                >
                    <div className="flex items-center gap-3 mb-3">
                        <button
                            type="button"
                            disabled={kiLaeuft || gesperrt || isReadOnly}
                            onClick={() => void erkundungVorschlagen()}
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-stone-700 text-white rounded-lg text-xs font-bold disabled:opacity-40"
                        >
                            {kiLaeuft
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Wand2 className="w-3.5 h-3.5" />}
                            {kiLaeuft ? 'Erarbeitet …' : 'Vorschlag erarbeiten'}
                        </button>
                        {vorschlag && (
                            <button
                                type="button"
                                onClick={() => setVorschlag(null)}
                                className="text-xs text-slate-500 underline"
                            >
                                verwerfen
                            </button>
                        )}
                    </div>

                    {kiFehler && <Fehlerzeile text={kiFehler} />}

                    {vorschlag && (
                        <div className="space-y-3">
                            {vorschlag.nutzungsgeschichte && (
                                <VorschlagsBlock
                                    titel="Nutzungsgeschichte (Entwurf aus Ihren Angaben)"
                                    text={vorschlag.nutzungsgeschichte}
                                    gesperrt={gesperrt || isReadOnly}
                                    onUebernehmen={() => void a.planAendern({
                                        historische_erkundung: {
                                            ...plan.historische_erkundung,
                                            nutzungsgeschichte: vorschlag.nutzungsgeschichte,
                                        },
                                    })}
                                />
                            )}

                            {vorschlag.offene_fragen.length > 0 && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                    <p className="text-xs font-bold text-amber-900 mb-1.5">
                                        Das lässt sich nur durch Recherche klären
                                    </p>
                                    <ul className="space-y-1">
                                        {vorschlag.offene_fragen.map((f, i) => (
                                            <li key={i} className="text-xs text-amber-900">
                                                • {f.frage}
                                                {f.quelle && <span className="text-amber-700"> — {f.quelle}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        type="button"
                                        disabled={gesperrt || isReadOnly}
                                        onClick={() => void a.planAendern({
                                            historische_erkundung: {
                                                ...plan.historische_erkundung,
                                                luecken: lueckenText(vorschlag),
                                                luecken_folge: vorschlag.luecken_folge
                                                    || String(plan.historische_erkundung.luecken_folge ?? ''),
                                            },
                                        })}
                                        className="mt-2 text-[11px] font-bold text-amber-900 underline disabled:opacity-40"
                                    >
                                        als Lücken übernehmen
                                    </button>
                                </div>
                            )}

                            {vorschlag.erwartete_stoffe.length > 0 && (
                                <div className="rounded-lg border border-slate-200 p-3">
                                    <p className="text-xs font-bold text-slate-700 mb-1.5">
                                        Zu erwartende Stoffe — jeweils mit Herleitung
                                    </p>
                                    <ul className="space-y-1">
                                        {vorschlag.erwartete_stoffe.map((st, i) => (
                                            <li key={i} className="text-xs text-slate-700">
                                                • <b>{st.stoff}</b> — {st.herleitung}
                                                {st.katalog_key && (
                                                    <span className="text-slate-400"> ({st.katalog_key})</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="text-[10px] text-slate-500 mt-1.5">
                                        Eine Erwartung ist kein Befund. Sie begründet, wonach zu suchen ist.
                                    </p>
                                </div>
                            )}

                            {vorschlag.gebaeudeschadstoffe.length > 0 && (
                                <VorschlagsBlock
                                    titel="Gebäudeschadstoffe, die zu prüfen sind"
                                    text={vorschlag.gebaeudeschadstoffe.join(', ')}
                                    gesperrt={gesperrt || isReadOnly}
                                    onUebernehmen={() => void a.planAendern({
                                        historische_erkundung: {
                                            ...plan.historische_erkundung,
                                            gebaeudeschadstoffe: vorschlag.gebaeudeschadstoffe,
                                        },
                                    })}
                                />
                            )}

                            {vorschlag.luecken_folge && (
                                <VorschlagsBlock
                                    titel="Welche Folge daraus gezogen wird"
                                    text={vorschlag.luecken_folge}
                                    gesperrt={gesperrt || isReadOnly}
                                    onUebernehmen={() => void a.planAendern({
                                        historische_erkundung: {
                                            ...plan.historische_erkundung,
                                            luecken_folge: vorschlag.luecken_folge,
                                        },
                                    })}
                                />
                            )}

                            {vorschlag.hinweise.length > 0 && (
                                <ul className="space-y-0.5">
                                    {vorschlag.hinweise.map((h, i) => (
                                        <li key={i} className="text-[10px] text-slate-500 italic">{h}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </Karte>
            )}

            {/* Die Notfallangaben stehen bei den Grundlagen und nicht bei den
                Aushängen: der Aushang DRUCKT sie nur. Wer sie dort erfasste,
                bemerkte erst beim Drucken, dass sie fehlen. */}
            {reiter === 'grundlagen' && (
                <NotfallKarte
                    bestand={bestand}
                    notfall={plan.notfall}
                    gesperrt={gesperrt || Boolean(isReadOnly)}
                    onSpeichern={n => void a.planAendern({ notfall: { ...plan.notfall, ...n } })}
                />
            )}

            {/* ─── Befunde ──────────────────────────────────────────────── */}
            {reiter === 'befunde' && (
                <Karte
                    titel="Rasterfelder, Proben und Messwerte"
                    hinweis="Eine Probe gilt für genau eine Schicht — aus der Tiefenlage folgt später die Schutzklasse."
                >
                    <BefundeEditor
                        felder={b.rasterfelder}
                        proben={b.proben}
                        gesperrt={gesperrt || isReadOnly}
                        feldAnlegen={nr => a.feldAnlegen({ feld_nr: nr })}
                        feldAendern={a.aendern.feld}
                        feldLoeschen={a.loeschen.feld}
                        probeAnlegen={a.probeAnlegen}
                        probeAendern={a.aendern.probe}
                        probeLoeschen={a.loeschen.probe}
                    />
                </Karte>
            )}

            {/* ─── Arbeitsbereiche ──────────────────────────────────────── */}
            {reiter === 'bereiche' && (
                <Karte
                    titel="Arbeitsbereiche und Tätigkeiten"
                    hinweis="Transport, Bereitstellungslager, Wasserhaltung, Abfallaufbereitung und Vermessung gehören dazu — sie werden am häufigsten übersehen."
                >
                    <BereicheEditor
                        cluster={b.cluster}
                        taetigkeiten={b.taetigkeiten}
                        felder={b.rasterfelder}
                        schutzklassen={b.schutzklassen}
                        lage={a.bereichsLage}
                        gesperrt={gesperrt || isReadOnly}
                        bereichAnlegen={k => a.bereichAnlegen({ kennung: k })}
                        bereichAendern={a.aendern.bereich}
                        bereichLoeschen={a.loeschen.bereich}
                        taetigkeitAnlegen={a.taetigkeitAnlegen}
                        taetigkeitAendern={a.aendern.taetigkeit}
                        taetigkeitLoeschen={a.loeschen.taetigkeit}
                    />
                </Karte>
            )}

            {/* ─── Schutzklassen ────────────────────────────────────────── */}
            {reiter === 'schutz' && (
                <>
                    <Karte
                        titel="Schutzklassen bearbeiten"
                        hinweis="Unbestimmte Begriffe wie „geeigneter Filteratemschutz“ sind keine Festlegung. Die Tragezeit wird hier gerechnet und an der Klasse gespeichert."
                    >
                        <SchutzklassenEditor
                            klassen={b.schutzklassen}
                            gesperrt={gesperrt || isReadOnly}
                            anlegen={a.schutzklasseAnlegen}
                            aendern={a.aendern.schutzklasse}
                            loeschen={a.loeschen.schutzklasse}
                        />
                    </Karte>

                    <Karte titel="Übersicht">
                        <ul className="space-y-3">
                            {[...b.schutzklassen].sort((x, y) => x.rang - y.rang).map(sk => (
                                <li key={sk.id} className="border border-slate-200 rounded-xl p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span
                                            className="w-3 h-3 rounded-sm border border-slate-300"
                                            style={{ background: sk.farbe }}
                                        />
                                        <span className="font-black text-slate-800">{sk.key}</span>
                                        <span className="text-sm text-slate-600">{sk.bezeichnung}</span>
                                    </div>
                                    <p className="text-xs text-slate-500 mb-2">{sk.anwendungsbereich}</p>
                                    <div className="grid sm:grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <p className="font-bold text-slate-700 mb-0.5">Mindest-PSA</p>
                                            <ul className="text-slate-600 space-y-0.5">
                                                {sk.psa.map((p, i) => <li key={i}>• {p.text}</li>)}
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-700 mb-0.5">Atemschutz (TRGS 524 Anlage 8)</p>
                                            <p className="text-slate-600">
                                                {sk.atemschutz.geraet
                                                    ? <>{sk.atemschutz.geraet}
                                                        {sk.atemschutz.filtertyp && <> · Filter {sk.atemschutz.filtertyp}</>}</>
                                                    : 'kein Atemschutz'}
                                            </p>
                                            {sk.tragezeit?.ergebnis_min && (
                                                <p className="text-slate-600 mt-0.5">
                                                    Tragezeit {sk.tragezeit.ergebnis_min} min ·
                                                    {' '}Erholung {sk.tragezeit.erholung_min} min
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </Karte>

                    <Karte
                        titel="Tragezeit ausrechnen"
                        hinweis="DGUV Regel 112-190 Anhang 2. Die Basiswerte gelten für mittlere Arbeitsschwere und etwa Raumtemperatur — im Schwarzbereich trifft beides selten zu."
                    >
                        <div className="flex flex-wrap gap-3 items-start">
                            <select
                                value={geraet}
                                onChange={e => setGeraet(e.target.value)}
                                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            >
                                {GERAETEGRUPPEN.map(g => (
                                    <option key={g.key} value={g.key}>
                                        {g.label}{g.beleg === 'zu_pruefen' ? ' (Wert fehlt)' : ''}
                                    </option>
                                ))}
                            </select>
                            <div className="flex flex-wrap gap-2">
                                {KORREKTUREN.map(k => (
                                    <label key={k.key} className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                                        <input
                                            type="checkbox"
                                            checked={korrekturen.includes(k.key)}
                                            onChange={e => setKorrekturen(v =>
                                                e.target.checked ? [...v, k.key] : v.filter(x => x !== k.key))}
                                        />
                                        {k.grund.split(' (')[0]}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
                            {tragezeit.verwendbar ? (
                                <>
                                    <p className="text-sm font-bold text-slate-800">
                                        {tragezeit.ergebnis_min} Minuten Einsatz, danach {tragezeit.erholung_min} Minuten Erholung
                                    </p>
                                    <p className="text-xs text-slate-600 mt-1">
                                        In einer Achtstundenschicht: {schicht.einsaetze} Einsätze,
                                        {' '}{schicht.nutzbar_min} Minuten unter Atemschutz nutzbar
                                        {' '}({Math.round(schicht.minderung * 100)} % Minderung).
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-amber-800">{tragezeit.hinweise[0]}</p>
                            )}
                            {[...tragezeit.hinweise.slice(tragezeit.verwendbar ? 0 : 1), ...schicht.hinweise].map((h, i) => (
                                <p key={i} className="text-xs text-slate-500 mt-1.5">{h}</p>
                            ))}
                        </div>
                    </Karte>
                </>
            )}

            {/* ─── Rollen und Stufungen ─────────────────────────────────── */}
            {reiter === 'rollen' && (
                <>
                    <Karte
                        titel="Rollen und ihre Situationen"
                        hinweis="Ein Baggerfahrer trägt in der belüfteten Kabine eine andere Klasse als außerhalb — deshalb hängt die Schutzklasse an der Situation, nicht an der Rolle."
                    >
                        <RollenEditor
                            rollen={b.rollen}
                            cluster={b.cluster}
                            schutzklassen={b.schutzklassen}
                            gesperrt={gesperrt || isReadOnly}
                            anlegen={a.rolleAnlegen}
                            aendern={a.aendern.rolle}
                            loeschen={a.loeschen.rolle}
                        />
                    </Karte>

                    <Karte
                        titel="Auf- und Abstufungen"
                        hinweis="Aufstufen darf die Aufsicht sofort. Abstufen nur der Koordinator, schriftlich und auf Nachweis — eine unvollständige Abstufung gilt nicht."
                    >
                        <StufungenEditor
                            stufungen={b.stufungen}
                            cluster={b.cluster}
                            schutzklassen={b.schutzklassen}
                            gesperrt={gesperrt || isReadOnly}
                            anlegen={a.stufungAnlegen}
                            aendern={a.aendern.stufung}
                        />
                    </Karte>
                </>
            )}

            {/* ─── Textkapitel ──────────────────────────────────────────── */}
            {reiter === 'texte' && (
                <Karte
                    titel="Fließtextkapitel"
                    hinweis="Bausteine tragen, was auf jeder Baustelle dieser Art gilt. Ein eigener Wortlaut friert gegen spätere Katalogänderungen ein."
                >
                    <BausteineEditor
                        wahl={b.bausteine}
                        regelwerk={plan.regelwerk}
                        gesperrt={gesperrt || isReadOnly}
                        setzen={(kapitel, key, u) => void a.bausteinSetzen(kapitel, key, u)}
                    />
                </Karte>
            )}

            {/* ─── Messkonzept ──────────────────────────────────────────── */}
            {reiter === 'messung' && (
                <Karte
                    titel="Messkonzept"
                    hinweis="Jede Messung braucht ein Ziel nach TRGS 524 Anlage 9 — eine Messung ohne Ziel erzeugt nur vorgetäuschte Sicherheit."
                >
                    <MesskonzeptEditor
                        zeilen={plan.messkonzept}
                        gesperrt={gesperrt || isReadOnly}
                        onSpeichern={z => void a.planAendern({ messkonzept: z })}
                    />
                </Karte>
            )}

            {/* ─── Beteiligte, Unternehmen, Unterlagen ──────────────────── */}
            {reiter === 'beteiligte' && (
                <>
                    <Karte
                        titel="Beteiligte"
                        hinweis="Anhang 3 Nr. 1: Auftraggeber, Arbeitsschutzbehörde, Gutachter sowie der Koordinator mit seinen Weisungsbefugnissen."
                    >
                        <BeteiligteEditor
                            beteiligte={b.beteiligte}
                            unternehmen={b.unternehmen}
                            gesperrt={gesperrt || isReadOnly}
                            anlegen={a.beteiligtenAnlegen}
                            aendern={a.aendern.beteiligter}
                            loeschen={a.loeschen.beteiligter}
                        />
                    </Karte>

                    <BestandKarte
                        bestand={bestand}
                        beteiligte={b.beteiligte}
                        gesperrt={gesperrt || Boolean(isReadOnly)}
                        uebernehmen={a.beteiligteUebernehmen}
                    />

                    <Karte
                        titel="Unternehmen und Aufsichtsführende"
                        hinweis="Ohne unterschriebene Verpflichtungserklärung und Sachkundenachweis darf niemand in kontaminierten Bereichen beginnen."
                    >
                        <UnternehmenEditor
                            unternehmen={b.unternehmen}
                            gesperrt={gesperrt || isReadOnly}
                            anlegen={a.unternehmenAnlegen}
                            aendern={a.aendern.unternehmen}
                            loeschen={a.loeschen.unternehmen}
                        />
                    </Karte>

                    <Karte
                        titel="Unterlagen und Anlagen"
                        hinweis="„Wird nachgereicht“ ist ein Zustand und gehört ausgewiesen — nicht weggelassen."
                    >
                        <UnterlagenEditor
                            unterlagen={b.unterlagen}
                            gesperrt={gesperrt || isReadOnly}
                            anlegen={a.unterlageAnlegen}
                            ausKatalog={a.unterlagenAusKatalog}
                            aendern={a.aendern.unterlage}
                            loeschen={a.loeschen.unterlage}
                        />
                    </Karte>

                    {/* Die Bruecke zur Projektablage. Sie steht unter den
                        Unterlagen, weil sie deren Nachweisstand fuehrt: was
                        Kapitel 9 als „liegt vor" druckt, entscheidet sich hier
                        — und nicht an einem Haken, den jemand zu setzen
                        vergisst. */}
                    <AblageKarte
                        projectId={projectId}
                        bundle={b}
                        steckbrief={steckbrief}
                        bestand={bestand}
                        projektName={projektName}
                        projektNummer={projektNummer}
                        projektAdresse={projektAdresse}
                        schleuse={schleuse}
                        company={company}
                        quoteConfig={quoteConfig}
                        invoiceConfig={invoiceConfig}
                        gesperrt={gesperrt || Boolean(isReadOnly)}
                        nurAnsicht={Boolean(isReadOnly)}
                        benutzerId={userProfile?.id}
                        benutzerName={benutzer}
                        ctx={a.ctx}
                        schreiben={a.schreiben}
                    />
                </>
            )}

            {/* ─── Zeichnungen ──────────────────────────────────────────── */}
            {reiter === 'zeichnungen' && (
                <>
                    <Karte
                        titel="Schwarz-Weiß-Anlage"
                        hinweis="Die Reihenfolge der Kammern ist ihr ganzer Sinn: ablegen, reinigen, saubere Kleidung anlegen."
                    >
                        <div className="flex flex-wrap gap-2 mb-3">
                            {schleuse.kammern.map((k, i) => (
                                <select
                                    key={i}
                                    value={k.art}
                                    disabled={gesperrt || isReadOnly}
                                    onChange={e => setSchleuse(s => ({
                                        ...s,
                                        kammern: s.kammern.map((x, j) =>
                                            j === i ? { ...x, art: e.target.value as 'schwarz' | 'grau' | 'weiss' } : x),
                                    }))}
                                    className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs"
                                >
                                    <option value="schwarz">Kammer {i + 1}: schwarz</option>
                                    <option value="grau">Kammer {i + 1}: grau</option>
                                    <option value="weiss">Kammer {i + 1}: weiß</option>
                                </select>
                            ))}
                            <button
                                type="button"
                                disabled={gesperrt || isReadOnly}
                                onClick={() => setSchleuse(s => ({
                                    ...s,
                                    kammern: [...s.kammern, {
                                        name: `Kammer ${s.kammern.length + 1}`, art: 'weiss', laenge_m: 2,
                                    }],
                                }))}
                                className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs font-bold"
                            >
                                + Kammer
                            </button>
                            {schleuse.kammern.length > 2 && (
                                <button
                                    type="button"
                                    disabled={gesperrt || isReadOnly}
                                    onClick={() => setSchleuse(s => ({ ...s, kammern: s.kammern.slice(0, -1) }))}
                                    className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs font-bold"
                                >
                                    − Kammer
                                </button>
                            )}
                        </div>

                        {!gesperrt && !isReadOnly && (
                            <div className="flex items-center gap-3 mb-3">
                                <button
                                    type="button"
                                    onClick={() => void a.skizzeSpeichern(
                                        'schleuse', schleusenBild.titel, schleuse as unknown as Record<string, unknown>)}
                                    className="px-3 py-1.5 bg-stone-700 text-white rounded-lg text-xs font-bold"
                                >
                                    Einstellung speichern
                                </button>
                                <span className="text-[11px] text-slate-500">
                                    {b.skizzen.some(x => x.typ === 'schleuse')
                                        ? 'Gespeichert — der Plan und die Aushänge nutzen diese Einstellung.'
                                        : 'Noch nicht gespeichert: Plan und Aushänge zeigen die Standardanlage.'}
                                </span>
                            </div>
                        )}

                        {schleusenFehler.length > 0 && (
                            <div className="mb-3 space-y-1">
                                {schleusenFehler.map((f, i) => (
                                    <p key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {f}
                                    </p>
                                ))}
                            </div>
                        )}

                        <SkizzeSvg zeichnung={schleusenBild} />
                    </Karte>

                    <Karte
                        titel="Zonenplan"
                        hinweis="Der Graubereich ist kein dritter Ort, sondern die Grenze: jeder Weg aus dem Schwarzbereich führt hindurch."
                    >
                        <ZonenplanKarte
                            skizzen={b.skizzen}
                            bereiche={b.cluster.map(c => c.kennung)}
                            gesperrt={gesperrt || isReadOnly}
                            onSpeichern={a.skizzeSpeichern}
                        />
                    </Karte>

                    {rasterBild && (
                        <Karte titel="Rasterplan aus den Befunden">
                            <SkizzeSvg zeichnung={rasterBild} />
                        </Karte>
                    )}

                    {b.rasterfelder.map(f => {
                        const proben = b.proben.filter(p => p.rasterfeld_id === f.id);
                        if (proben.length === 0) return null;
                        const bereich = b.cluster.find(c => c.felder.includes(f.feld_nr));
                        return (
                            <Karte
                                key={f.id}
                                titel={`Schichtenschnitt Feld ${f.feld_nr}`}
                                hinweis="Aushubsohle, Überaushub, Trennlage und Abdeckung kommen aus dem Arbeitsbereich — Reiter „Bereiche“."
                            >
                                <SkizzeSvg
                                    zeichnung={zeichneSchnitt(
                                        schnittParameterAusBereich(f.feld_nr, bereich), proben)}
                                />
                            </Karte>
                        );
                    })}

                    <Karte
                        titel="Ablauf Bodenmanagement"
                        hinweis="Fast jeder Fehler im Umgang mit belastetem Aushub ist ein Reihenfolgefehler — deshalb eine Kette und keine Aufzählung."
                    >
                        <AblaufKarte
                            skizzen={b.skizzen}
                            gesperrt={gesperrt || isReadOnly}
                            onSpeichern={a.skizzeSpeichern}
                        />
                    </Karte>
                </>
            )}

            {/* ─── Aushänge ───────────────────────────── */}
            {/* Der Reiter liegt vollstaendig in `aushaenge/AushaengeReiter`.
                Klinik, Durchgangsarzt und Ersthelfer kommen aus dem Notrufplan
                des SiGe-Plans (Plan Abschnitt 10: „lesen — EINE Quelle") und
                werden HIER gelesen, nicht dort: sonst haelt das Modul denselben
                Notrufplan in mehreren Staenden. Gespeichert wird nichts. */}
            {reiter === 'aushaenge' && (
                <AushaengeReiter
                    bestand={bestand}
                    projektName={projektName || 'Baustelle'}
                    projektNummer={projektNummer}
                    bundle={b}
                    stoffe={aktiveStoffnamen}
                    schleuse={schleuse}
                />
            )}

            {/* ─── Formulare ────────────────────────────── */}
            {reiter === 'formulare' && (() => {
                // Die Ableitungen stehen bewusst hier und nicht in einem
                // `useMemo` weiter oben: sie werden nur auf diesem Reiter
                // gebraucht, sind reine Listenoperationen, und jede Aussage
                // darin — welches Formular verfuegbar ist, welche Angabe
                // fehlt, welcher Sollwert gedruckt werden darf — kommt aus
                // `formularKatalog.ts` und ist dort geprueft.
                const kEintrag = b.beteiligte.find(p => p.funktion_key === 'koordinator_dguv');
                const koordinator = kEintrag
                    ? [kEintrag.person, kEintrag.organisation].filter(Boolean).join(', ')
                    : '';
                const bereiche = b.cluster.filter(c => c.art !== 'abgegrenzt');
                const schutzklassenTexte = b.schutzklassen.map(
                    k => (k.bezeichnung && k.bezeichnung !== k.key
                        ? `${k.key} — ${k.bezeichnung}` : k.key));
                const baumassnahme = [...new Set(
                    bereiche.map(c => c.baumassnahme.trim()).filter(Boolean))].join('; ');
                const geraete = atemschutzGeraete(b);
                const freimessungGeregelt = plan.messkonzept.some(z => z.messziel === 'akut');

                return (
                    <>
                        {/* Nur der Formularbereich wandert in den Druck —
                            derselbe Weg wie bei den Aushaengen. */}
                        <style>{`
                            @media print {
                                body * { visibility: hidden; }
                                #asplan-formulare, #asplan-formulare * { visibility: visible; }
                                #asplan-formulare { position: absolute; inset: 0; }
                            }
                        `}</style>

                        <Karte
                            titel="Formulare"
                            hinweis="A4 hoch, zum Ausfüllen auf Papier. Was der Plan weiß, steht vorgedruckt drauf; was er nicht weiß, ist auf dem Blatt als offen ausgewiesen — eine blanke Linie sähe aus wie eine Aufgabe des Ausfüllenden."
                        >
                            <ul className="space-y-1.5 mb-3">
                                {verfuegbareFormulare(b).map(x => (
                                    <li key={x.key} className="text-sm flex items-start gap-2">
                                        <span className="text-slate-400">·</span>
                                        <span>
                                            <b className="text-slate-800">{x.titel}</b>
                                            <span className="block text-xs text-slate-500">{x.ort}</span>
                                            {x.fehlt && (
                                                <span className="block text-xs text-amber-700 mt-0.5">{x.fehlt}</span>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                onClick={() => window.print()}
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-stone-700 text-white rounded-lg text-xs font-bold"
                            >
                                <Printer className="w-3.5 h-3.5" /> Alle Formulare drucken
                            </button>
                        </Karte>

                        <div id="asplan-formulare" data-glass-papier>
                            {/* Je Auftragnehmer eine Erklärung und eine Anzeige.
                                Ist noch keiner erfasst, wird trotzdem gedruckt —
                                der leere Vordruck zeigt, was fehlt. */}
                            {(b.unternehmen.length > 0 ? b.unternehmen : [undefined]).map((u, i) => (
                                <FormularVerpflichtung
                                    key={u?.id ?? `blanko-${i}`}
                                    projektName={projektName || 'Baustelle'}
                                    projektNummer={projektNummer}
                                    projektAdresse={projektAdresse}
                                    planTitel={plan.titel}
                                    revLabel={plan.rev_label}
                                    stand={plan.stand}
                                    koordinator={koordinator}
                                    unternehmen={u}
                                />
                            ))}

                            {(b.unternehmen.length > 0 ? b.unternehmen : [undefined]).map((u, i) => (
                                <FormularAnzeige
                                    key={u?.id ?? `blanko-${i}`}
                                    projektName={projektName || 'Baustelle'}
                                    projektNummer={projektNummer}
                                    projektAdresse={projektAdresse}
                                    unternehmen={u}
                                    bg={u ? bgDesUnternehmens(b, u) : ''}
                                    stoffe={aktiveStoffnamen}
                                    baumassnahme={baumassnahme}
                                    schutzklassen={schutzklassenTexte}
                                    koordinator={koordinator}
                                />
                            ))}

                            <FormularSchleusenbuch
                                projektName={projektName || 'Baustelle'}
                                bereiche={bereiche.map(c => ({
                                    kennung: c.kennung, schutzklasse: c.schutzklasse_key,
                                }))}
                            />

                            <FormularFilterbuch
                                projektName={projektName || 'Baustelle'}
                                zeilen={filterZeilen(b)}
                            />

                            <FormularTragezeit
                                projektName={projektName || 'Baustelle'}
                                zeilen={geraete}
                            />

                            <FormularHeissarbeit
                                projektName={projektName || 'Baustelle'}
                                stoffe={aktiveStoffnamen}
                                freimessungGeregelt={freimessungGeregelt}
                            />

                            <FormularUnterweisung
                                projektName={projektName || 'Baustelle'}
                                planTitel={plan.titel}
                                revLabel={plan.rev_label}
                                stand={plan.stand}
                                schutzklassen={schutzklassenTexte}
                            />

                            <FormularUebergabe
                                projektName={projektName || 'Baustelle'}
                                projektNummer={projektNummer}
                                bereiche={bereiche.map(c => ({
                                    kennung: c.kennung, bezeichnung: c.bezeichnung,
                                }))}
                                koordinator={koordinator}
                            />
                        </div>
                    </>
                );
            })()}

            {/* ─── Selbstcheck ──────────────────────────────────────────── */}
            {reiter === 'check' && (
                <Karte
                    titel="Selbstcheck gegen die bekannten Mängel"
                    hinweis="Aufgearbeitet von der BG BAU (Bauportal 1/2021). Automatisch beurteilt wird nur das Vorhandensein — nie die fachliche Angemessenheit."
                >
                    <PrueflisteEditor
                        stand={b.pruefliste}
                        auto={a.checks}
                        gesperrt={gesperrt || isReadOnly}
                        setzen={(key, status, bemerkung) =>
                            void a.pruefstandSetzen(key, status, bemerkung)}
                    />
                </Karte>
            )}

            <p className="text-[10px] text-slate-400 text-center mt-6">
                Katalogfassung {aktuelleKatalogVersion()}
                {plan.katalog_version !== aktuelleKatalogVersion()
                    && ` · dieser Plan wurde mit ${plan.katalog_version} aufgestellt`}
            </p>
        </Rahmen>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// Bausteine der Oberflaeche
// ═══════════════════════════════════════════════════════════════════════════

const Rahmen: React.FC<{
    onBack: () => void; titel: string; untertitel?: string; children: React.ReactNode;
}> = ({ onBack, titel, untertitel, children }) => (
    // `min-h-0` an beiden Ebenen: ein Flex-Kind hat `min-height: auto` und
    // schrumpft nicht unter seinen Inhalt, dann greift `overflow-y-auto` nicht.
    <div data-glass-view="module-asplan" data-glass-bereich="module" className="h-full flex flex-col bg-slate-50 min-h-0">
        <div className="flex-1 overflow-y-auto min-h-0">
            <div className="max-w-5xl mx-auto px-4 py-6">
                <div data-glass="titel" className="flex items-start gap-4 mb-6">
                    <button
                        type="button"
                        onClick={onBack}
                        className="mt-1 p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
                        aria-label="Zurück"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="min-w-0 flex-1">
                        <h1 className="text-2xl font-black text-slate-900 leading-tight">{titel}</h1>
                        {untertitel && <p className="text-sm text-slate-500">{untertitel}</p>}
                    </div>
                </div>
                {children}
            </div>
        </div>
    </div>
);

const Karte: React.FC<{ titel: string; hinweis?: string; children: React.ReactNode }> = ({
    titel, hinweis, children,
}) => (
    <section className="bg-white border border-slate-200 rounded-2xl p-4 mb-4">
        <h2 className="font-bold text-slate-800 mb-0.5">{titel}</h2>
        {hinweis && <p className="text-xs text-slate-500 mb-3">{hinweis}</p>}
        {children}
    </section>
);

const Kachel: React.FC<{ wert: number | string; label: string; ton: 'rot' | 'amber' | 'gruen' | 'grau' }> = ({
    wert, label, ton,
}) => {
    const farben = {
        rot: 'bg-red-50 border-red-200 text-red-700',
        amber: 'bg-amber-50 border-amber-200 text-amber-700',
        gruen: 'bg-emerald-50 border-emerald-200 text-emerald-700',
        grau: 'bg-slate-50 border-slate-200 text-slate-600',
    }[ton];
    return (
        <div className={`rounded-xl border px-3 py-2.5 ${farben}`}>
            <p className="text-xl font-black leading-none">{wert}</p>
            <p className="text-[11px] mt-1">{label}</p>
        </div>
    );
};

const Hinweis: React.FC<{ ton: 'amber' | 'emerald'; children: React.ReactNode }> = ({ ton, children }) => (
    <div className={`mb-4 rounded-xl px-4 py-2.5 text-sm border ${
        ton === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-emerald-50 border-emerald-200 text-emerald-800'
    }`}>
        {children}
    </div>
);

/**
 * Der A+S-Bedarf aus der Planungsphase im Modulkopf.
 *
 * Die Worte kommen aus `planungBedarf.ts` und sind dort geprueft; hier wird
 * nur gefaerbt. Vor `bereit` steht „wird gelesen" — nicht nichts, denn aus
 * dem Fehlen des Hinweises liesse sich „kein Bedarf" lesen. Ein Lesefehler
 * ist rot wie die Pflicht: aus einem Ausfall folgt keine Entwarnung.
 */
const BEDARF_FARBEN: Record<Bedarfsanzeige['stand'], string> = {
    pflicht: 'bg-red-50 border-red-200 text-red-800',
    ungeklaert: 'bg-amber-50 border-amber-200 text-amber-800',
    keine: 'bg-slate-50 border-slate-200 text-slate-600',
    lesefehler: 'bg-red-50 border-red-200 text-red-800',
};

const BedarfsHinweis: React.FC<{ bedarf: Bedarfsanzeige | null; bereit: boolean }> = ({ bedarf, bereit }) => {
    if (!bereit || !bedarf) {
        return (
            <p className="mb-4 text-xs text-slate-400 inline-flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Planungsphase wird gelesen …
            </p>
        );
    }
    return (
        <div className={`mb-4 rounded-xl px-4 py-2.5 border ${BEDARF_FARBEN[bedarf.stand]}`}>
            <p className="text-sm font-bold flex items-center gap-1.5">
                {bedarf.stand === 'keine'
                    ? <Info className="w-4 h-4 shrink-0" />
                    : <AlertTriangle className="w-4 h-4 shrink-0" />}
                {bedarf.titel}
            </p>
            <p className="text-xs mt-1">{bedarf.begruendung}</p>
            {bedarf.rechtsgrundlage && (
                <p className="text-[10px] mt-0.5 opacity-70">{bedarf.rechtsgrundlage}</p>
            )}
        </div>
    );
};

/**
 * Ein Vorschlag mit Übernehmen-Knopf.
 *
 * Übernehmen ist ein eigener Klick und nicht die Vorgabe: bei der
 * Historischen Erkundung trägt der Koordinator die Verantwortung für den
 * Wortlaut, und ein Text, der ungefragt im Feld steht, wird beim
 * Durchsehen mit „stimmt schon" quittiert.
 */
const VorschlagsBlock: React.FC<{
    titel: string;
    text: string;
    /** Warum der Vorschlag mit Vorsicht zu lesen ist (Ersatzquelle) — steht sichtbar dabei. */
    vorbehalt?: string;
    /** Beschriftung des Knopfs; Vorgabe „übernehmen". */
    knopf?: string;
    gesperrt?: boolean;
    onUebernehmen: () => void;
}> = ({ titel, text, vorbehalt, knopf = 'übernehmen', gesperrt, onUebernehmen }) => (
    <div className="rounded-lg border border-slate-200 p-3">
        <p className="text-xs font-bold text-slate-700 mb-1">{titel}</p>
        <p className="text-sm text-slate-800 whitespace-pre-wrap">{text}</p>
        {vorbehalt && (
            <p className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /> {vorbehalt}
            </p>
        )}
        <button
            type="button"
            disabled={gesperrt}
            onClick={onUebernehmen}
            className="mt-2 text-[11px] font-bold text-stone-700 underline disabled:opacity-40"
        >
            {knopf}
        </button>
    </div>
);

const Fehlerzeile: React.FC<{ text: string }> = ({ text }) => (
    <div className="mb-4 rounded-xl px-4 py-2.5 text-sm bg-red-50 border border-red-200 text-red-800">
        {text}
    </div>
);


/**
 * Ein Textfeld, das beim Verlassen speichert.
 *
 * Bewusst NICHT bei jedem Tastendruck: jeder Schreibvorgang laedt das Buendel
 * neu, und ein Neuladen bei jedem Buchstaben nimmt dem Feld den Fokus. Genau
 * dieser Fehler hat im Repo schon einmal einen Editor unbrauchbar gemacht.
 */
const Feld: React.FC<{
    label: string;
    wert: string;
    gesperrt?: boolean;
    mehrzeilig?: boolean;
    hinweis?: string;
    onSpeichern: (wert: string) => void;
}> = ({ label, wert, gesperrt, mehrzeilig, hinweis, onSpeichern }) => {
    const [entwurf, setEntwurf] = useState(wert);
    // Der Entwurf folgt dem gespeicherten Wert nur, solange nicht getippt
    // wird — sonst ueberschriebe ein Neuladen die laufende Eingabe.
    const [getippt, setGetippt] = useState(false);
    const anzeige = getippt ? entwurf : wert;

    return (
        <label className="block mb-3">
            <span className="block text-xs font-bold text-slate-700 mb-1">{label}</span>
            {hinweis && <span className="block text-[11px] text-slate-500 mb-1">{hinweis}</span>}
            {mehrzeilig ? (
                <textarea
                    value={anzeige}
                    disabled={gesperrt}
                    rows={3}
                    onChange={e => { setGetippt(true); setEntwurf(e.target.value); }}
                    onBlur={() => { if (getippt && entwurf !== wert) onSpeichern(entwurf); setGetippt(false); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                />
            ) : (
                <input
                    value={anzeige}
                    disabled={gesperrt}
                    onChange={e => { setGetippt(true); setEntwurf(e.target.value); }}
                    onBlur={() => { if (getippt && entwurf !== wert) onSpeichern(entwurf); setGetippt(false); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-50"
                />
            )}
        </label>
    );
};
